import React from "react";
import moment from "moment";
import { MdClose, MdReceiptLong } from "react-icons/md";

const LostProductDetailsModal = ({ open, onClose, lostProduct }) => {
  if (!open || !lostProduct) return null;

  const details = lostProduct.details || [];

  const totalBeforeDiscount = details.reduce(
    (sum, d) => sum + parseFloat(d.total_price || 0),
    0
  );
  const totalDiscount = details.reduce(
    (sum, d) => sum + parseFloat(d.discount || 0) * parseFloat(d.quantity || 1),
    0
  );
  const totalAfterDiscount = details.reduce(
    (sum, d) => sum + parseFloat(d.discount_price || 0),
    0
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');
        .lpd-serif { font-family: 'DM Serif Display', serif; }
        @keyframes lpdFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes lpdSlideUp {
          from { opacity: 0; transform: translateY(24px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .lpd-overlay { animation: lpdFadeIn 0.2s ease both; }
        .lpd-panel { animation: lpdSlideUp 0.25s ease both; }
      `}</style>

      <div
        className="lpd-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        onClick={onClose}
        style={{ fontFamily: "'DM Sans', sans-serif" }}
      >
        <div
          className="lpd-panel bg-white rounded-2xl shadow-xl w-full max-w-[900px] max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-6 py-5 border-b border-[#eef1f6]">
            <div className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center"
                style={{ background: "linear-gradient(135deg, #1F2937, #111827)" }}
              >
                <MdReceiptLong size={18} className="text-white" />
              </div>
              <h2 className="lpd-serif text-[#0a2540] text-[22px]">Lost Product Details</h2>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-[#8a9bb0] hover:bg-[#f4f7fb] hover:text-[#0a2540] transition-colors"
            >
              <MdClose size={20} />
            </button>
          </div>
          <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-3 gap-4 border-b border-[#eef1f6]">
            <div>
              <span className="block text-[11px] font-semibold uppercase tracking-[0.7px] text-[#8a9bb0] mb-1">
                Month Name
              </span>
              <span className="text-[14px] font-semibold text-[#0a2540]">
                {lostProduct.customer_name || "—"}
              </span>
            </div>
            <div>
              <span className="block text-[11px] font-semibold uppercase tracking-[0.7px] text-[#8a9bb0] mb-1">
                Date
              </span>
              <span className="text-[14px] font-semibold text-[#0a2540]">
                {lostProduct.date ? moment(lostProduct.date).format("ll") : "—"}
              </span>
            </div>
            <div>
              <span className="block text-[11px] font-semibold uppercase tracking-[0.7px] text-[#8a9bb0] mb-1">
                Amount
              </span>
              <span className="text-[14px] font-semibold text-[#1F2937]">
                Rs. {parseFloat(lostProduct.amount || 0).toLocaleString("en-PK", { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>
          <div className="px-6 py-5">
            <h3 className="text-[13px] font-semibold uppercase tracking-[0.8px] text-[#4a6080] mb-4">
              Lost Items
            </h3>

            <div className="overflow-x-auto rounded-xl border border-[#eef1f6]">
              <table className="w-full text-left">
                <thead>
                  <tr style={{ background: "#1F2937" }}>
                    {["Sr.No", "Item Name", "Batch No.", "Qty", "Expiry", "Sale Price", "Discount", "Total"].map((h) => (
                      <th
                        key={h}
                        className="px-3 py-2.5 text-[11px] font-semibold uppercase tracking-[0.6px] text-white whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {details.length > 0 ? (
                    details.map((item, index) => (
                      <tr
                        key={index}
                        className={`text-[13px] border-b border-[#f0f4f8] ${index % 2 === 0 ? "bg-white" : "bg-[#fafbfd]"}`}
                      >
                        <td className="px-3 py-2.5 font-bold text-[#1F2937]">{index + 1}</td>
                        <td className="px-3 py-2.5 font-semibold text-[#0a2540] whitespace-nowrap">
                          {item.medicine_name || item.itemName || "—"}
                        </td>
                        <td className="px-3 py-2.5 text-[#4a6080]">{item.batch_no}</td>
                        <td className="px-3 py-2.5 text-[#4a6080]">{item.quantity}</td>
                        <td className="px-3 py-2.5 text-[#4a6080] whitespace-nowrap">
                          {item.expiry_date ? moment(item.expiry_date).format("ll") : "—"}
                        </td>
                        <td className="px-3 py-2.5 font-semibold text-[#0a2540]">
                          Rs. {parseFloat(item.sale_price || 0).toFixed(2)}
                        </td>
                        <td className="px-3 py-2.5 font-semibold text-[#1F2937]">
                          Rs. {parseFloat(item.discount || 0).toFixed(2)}
                        </td>
                        <td className="px-3 py-2.5 font-bold text-[#0a2540]">
                          Rs. {parseFloat(item.discount_price || item.total_price || 0).toFixed(2)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center py-8 text-[#b0bcc9] text-sm">
                        No item details available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="px-6 pb-6">
            <div className="grid grid-cols-3 gap-4">
              <div className="rounded-xl p-4 border border-[#eef1f6] bg-[#f4f7fb]">
                <span className="block text-[11px] font-semibold uppercase tracking-[0.7px] text-[#8a9bb0] mb-1">
                  Total Amount
                </span>
                <span className="lpd-serif text-[18px] font-bold text-[#0a2540]">
                  Rs. {totalBeforeDiscount.toFixed(2)}
                </span>
              </div>
              <div className="rounded-xl p-4 border border-orange-100 bg-[#fff7f0]">
                <span className="block text-[11px] font-semibold uppercase tracking-[0.7px] text-[#8a9bb0] mb-1">
                  Total Discount
                </span>
                <span className="lpd-serif text-[18px] font-bold text-[#e96f1f]">
                  Rs. {totalDiscount.toFixed(2)}
                </span>
              </div>
              <div className="rounded-xl p-4 border border-[#dde4ed] bg-[#f4f7fb]">
                <span className="block text-[11px] font-semibold uppercase tracking-[0.7px] text-[#8a9bb0] mb-1">
                  Net Amount
                </span>
                <span className="lpd-serif text-[18px] font-bold text-[#1F2937]">
                  Rs. {totalAfterDiscount.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LostProductDetailsModal;
